import { useEffect, useState } from 'react'

import { TeslaAccountIdGuide } from './TeslaAccountIdGuide'
import { TeslaIntegrationSetupAlert } from './TeslaIntegrationSetupAlert'
import { TeslaIntegrationSetupPopup } from './TeslaIntegrationSetupPopup'
import {
    Button,
    Checkbox,
    ClickToCopy,
    Form,
    FormErrorsType,
    IconCopy02,
    IconEye,
    IconEyeOff,
    IntegrationMaintainerForm,
    IntegrationMaintainerFormDataType,
    TeslaForm,
    TeslaFormData
} from '@/components'
import { IntegrationCredentialStatusEnum, InverterManufacturerEnum } from '@/enums'
import { useToastNotifications } from '@/hooks'
import { Company, Integration } from '@/models'
import { isApiError } from '@/services'
import { FormChangeEvent } from '@/types'

type EditTeslaFormProps = {
    company: Company
    integration?: Integration

    onChange: () => void
    onAdded: () => void
}

interface EditTeslaFormData extends IntegrationMaintainerFormDataType, TeslaFormData {}

const EditTeslaForm = ({
    integration,
    company,
    ...props
}: EditTeslaFormProps) => {
    const { error } = useToastNotifications()
    const [mode, setMode] = useState<'view' | 'edit'>(integration?.status.isValid ||
        integration?.status.isUsesMaintainer ? 'view' : 'edit')

    const getForm = (): EditTeslaFormData => ({
        company_id: company.id,
        provider: InverterManufacturerEnum.TESLA,
        account_id: integration?.account_id || '',
        contact_name: integration?.contact_name || '',
        contact_email: integration?.contact_email || '',
        status: integration?.contact_name && integration?.contact_email
            ? IntegrationCredentialStatusEnum.USES_MAINTAINER : ''
    })
    const [form, setForm] = useState<EditTeslaFormData>(getForm())
    const isUsesMaintainer = form.status === IntegrationCredentialStatusEnum.USES_MAINTAINER
    const [processing, setProcessing] = useState(false)
    const [errors, setErrors] = useState<FormErrorsType>({})
    const [isStep2Passed, setIsStep2Passed] = useState(isUsesMaintainer)
    const [showAccountId, setShowAccountId] = useState(false)

    const handleSubmit = async () => {
        setProcessing(true)
        setErrors({})

        try {
            if (integration) {
                await integration.update(form)
            } else {
                await Integration.store(form)
            }
            setMode('view')
            await props.onChange()
            props.onAdded()
        } catch (err) {
            if (!isApiError(err)) throw err
            error('Something went wrong!')
            if (err.errors) {
                setErrors(err.errors)
            } else {
                throw err
            }
        } finally {
            setProcessing(false)
        }
    }

    const handleChange = (e: FormChangeEvent) =>
        setForm({ ...form, [e.target.name]: e.target.value })

    const handleClickRemove = async () => {
        setProcessing(true)
        try {
            await integration?.destroy()
            if (props.onChange) {
                await props.onChange()
            }
            setForm({
                ...form,
                contact_name: '',
                contact_email: ''
            })
            setMode('edit')
        } finally {
            setProcessing(false)
        }
    }

    useEffect(() => {
        setForm(getForm())
    }, [company])

    return mode === 'edit'
        ? <Form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            {integration?.status.isMissing && <>
                <p className="text-xs w-180 max-w-full">
                    Before submitting your confirmation, please provide Tesla Account ID and carefully review the Tesla
                    Integration Guide to ensure you understand all requirements and steps for smooth setup.
                </p>
                <p className="text-xs text-error-500 w-180 max-w-full">
                    A warranty enrollment was detected using inverter manufacturer. All certified providers must provide
                    Solar Insure access to stay compliant with our requirements.
                </p>
            </>}
            <h4 className="font-semibold text-gray-750 text-sm">
                1. Provide Your Tesla Account ID
            </h4>

            <div className="flex flex-col gap-4 md:ml-4 lg:ml-6 xl:ml-10">
                <TeslaForm
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    inputClassName="!max-w-85"
                />
                <TeslaAccountIdGuide/>
            </div>

            <h4 className="font-semibold text-gray-750 text-sm">
                2. Learn how to enable Tesla monitoring for each Solar Insure warranty.
            </h4>

            <div className="md:ml-4 lg:ml-6 xl:ml-10">
                <TeslaIntegrationSetupPopup
                    isCompleted={isStep2Passed}
                    onComplete={() => setIsStep2Passed(true)}
                />
            </div>
            <h4 className="font-semibold text-gray-750 text-sm">
                3. Acknowledge Integration Requirements & Sign Off
            </h4>

            <div className="md:ml-4 lg:ml-6 xl:ml-10 flex flex-col gap-4">
                <Checkbox
                    className="w-full"
                    label="I have read and understand that I must list Solar Insure as a Partner for all new Devices on the Tesla One App"
                    name="status"
                    checked={isUsesMaintainer}
                    value={isUsesMaintainer ? '' : IntegrationCredentialStatusEnum.USES_MAINTAINER}
                    onChange={handleChange}
                    disabled={!isStep2Passed}
                />
                <IntegrationMaintainerForm
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    disabled={!isStep2Passed || !isUsesMaintainer}
                    inputClassName="!max-w-85"
                    tooltip={!isStep2Passed || !isUsesMaintainer
                        ? <div>
                            You must acknowledge that you've read and<br/>
                            understood all instructions before continuing.
                        </div>
                        : undefined}
                />
            </div>

            <div className="flex gap-2">
                <Button
                    disabled={!isUsesMaintainer || integration?.status.isMissingOrNotRequired}
                    design={!isUsesMaintainer || integration?.status.isMissingOrNotRequired
                        ? 'btn-tertiary' : 'btn-secondary-gray'}
                    hasError={!(!isUsesMaintainer || integration?.status.isMissingOrNotRequired)}
                    onClick={() => setMode('view')}
                    type="button"
                >
                    Cancel
                </Button>
                <Button
                    processing={processing}
                    disabled={!isStep2Passed ||
                        !isUsesMaintainer ||
                        !form.contact_name ||
                        !form.contact_email ||
                        !form.account_id}
                >
                    Submit Credentials
                </Button>
            </div>
        </Form>
        : <div className="flex flex-col gap-6">
            <div className="flex flex-wrap gap-2 justify-between">
                <div className="flex flex-col gap-1.5">
                    <h4 className="text-xl font-normal">Tesla Account ID</h4>
                    <div className="flex gap-1 items-center break-anywhere">
                        {integration?.account_id
                            ? <>
                                {integration?.account_id?.split('')
                                    .map((item: string) => showAccountId ? item : '•')}
                                <button
                                    className="cursor-pointer"
                                    onClick={() => setShowAccountId(state => !state)}
                                >
                                    {showAccountId
                                        ? <IconEyeOff className="stroke-gray-500" size="sm"/>
                                        : <IconEye className="stroke-gray-500" size="sm"/>}
                                </button>
                                <ClickToCopy content={integration?.account_id}>
                                    <IconCopy02 className="stroke-gray-500" size="sm"/>
                                </ClickToCopy>
                            </>
                            : '-'}
                    </div>
                </div>
                {!integration?.status.isUsesMaintainer &&
                    <Button design="btn-link" className="!text-primary-800" onClick={() => setMode('edit')}>
                        Edit Credentials
                    </Button>}
            </div>
            <div className="flex flex-wrap gap-4 justify-between items-end">
                <Checkbox
                    className="w-full"
                    label={<div>
                        I have read and understand that I must list Solar Insure as a Partner for all new Devices on the
                        Tesla One App
                        <p className="text-sm text-gray-500">
                            {integration?.contact_name}&nbsp;
                            {integration?.contact_email}&nbsp;
                            {integration?.status_change_detected_at?.format()}
                        </p>
                    </div>}
                    name="status"
                    checked
                    disabled
                />

                <div className="flex flex-wrap gap-10">
                    <Button
                        design="btn-link"
                        hasError
                        className="whitespace-nowrap"
                        onClick={handleClickRemove}
                        disabled={processing}
                    >
                        Remove Signature
                    </Button>

                    <Button
                        design="btn-link"
                        className="!text-primary-800 whitespace-nowrap"
                        onClick={() => setMode('edit')}
                        disabled={processing}
                    >
                        Edit Signature
                    </Button>
                </div>
            </div>

            {integration && <TeslaIntegrationSetupAlert integration={integration}/>}
        </div>
}

export default EditTeslaForm
